import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent } from "react";
import { Link } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import type { FileDisputeResponse, PartyType, StatementLineItem, UploadEvidenceResponse } from "../../../api/types";
import { CommandError } from "../../../components/CommandError";
import { RejectionDetail } from "../../../components/RejectionDetail";
import { Banner, Button, Dialog, FieldShell, Money, StatusPill, Table, Textarea } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useIntentKey } from "../../../lib/useIntentKey";
import {
  disputesPath,
  EVIDENCE_ACCEPT,
  EVIDENCE_LIMITS_TEXT,
  EVIDENCE_MAX_BYTES,
  EVIDENCE_MAX_ITEMS,
  EVIDENCE_TYPES,
  formatBytes,
  MEDIA_LABEL,
  REASON_MAX_CHARS,
  SCAN_STATE,
  STUB_SCANNER_TEXT,
} from "./disputeCopy";

// "Dispute this line" (G-28 advertiser, G-43 publisher; UX-DESIGN.md §2.2,
// §3.6). Evidence goes up first, one file per POST /v1/evidence
// (dispute_evidence.go:134-227), and lands scanned in the same request in
// this build; only 'clean' evidence can be attached (file_statement_line_
// dispute, 0063:778-787). Then POST /v1/statements/{id}/lines/{line}/
// disputes {reason, evidence_ids} (disputes.go:100-149). Filing needs no
// step-up. The file name is the browser's: the server stores none.

interface Upload {
  /** Local row id. */
  key: string;
  name: string;
  size: number;
  state: "uploading" | "done" | "refused" | "failed";
  evidence?: UploadEvidenceResponse;
  error?: unknown;
  /** A refusal decided here, before sending. */
  localError?: string;
}

let uploadSeq = 0;

function localProblem(f: File): string | null {
  if (f.size === 0) return "This file is empty.";
  if (f.size > EVIDENCE_MAX_BYTES) return `This file is ${formatBytes(f.size)}, over the limit of 2 MB (${EVIDENCE_MAX_BYTES.toLocaleString("en-US")} bytes) a file.`;
  // The server decides by sniffing the bytes; an unknown browser type ("") is left to it.
  if (f.type && !(EVIDENCE_TYPES as string[]).includes(f.type)) return "Only PDF, PNG, JPEG and plain-text files are accepted.";
  return null;
}

export function DisputeLineDialog({
  statementId,
  line,
  partyType,
  home,
  onClose,
}: {
  statementId: string;
  line: StatementLineItem;
  partyType: PartyType;
  home: string;
  onClose: () => void;
}) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const fileInput = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [filed, setFiled] = useState<FileDisputeResponse | null>(null);
  const [showEvents, setShowEvents] = useState(false);
  const events = useQuery({
    queryKey: ["line-events", statementId, line.line_item_id],
    queryFn: () => ep.statements.lineEvents(statementId, line.line_item_id),
    enabled: showEvents,
  });

  const file = useMutation({
    mutationFn: (req: { reason: string; evidence_ids: string[] }) =>
      ep.disputes.file(statementId, line.line_item_id, req, { idempotencyKey: intent.keyFor(JSON.stringify(req)) }),
    onSuccess: (res) => {
      intent.reset();
      setFiled(res);
      void qc.invalidateQueries({ queryKey: ["statement", statementId] });
      void qc.invalidateQueries({ queryKey: ["disputes"] });
    },
  });

  const update = (key: string, patch: Partial<Upload>) => setUploads((us) => us.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  async function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const f of files) {
      const key = `u${++uploadSeq}`;
      const problem = localProblem(f);
      const row: Upload = { key, name: f.name, size: f.size, state: problem ? "refused" : "uploading", localError: problem ?? undefined };
      setUploads((us) => [...us, row]);
      if (problem) continue;
      try {
        // The same bytes are the same evidence server-side (result "existing"),
        // so a retry can never store a file twice, whatever the key.
        const ev = await ep.disputes.uploadEvidence(f);
        update(key, { state: "done", evidence: ev });
      } catch (err) {
        update(key, { state: "failed", error: err });
      }
    }
  }

  const attachable = uploads.filter((u) => u.state === "done" && u.evidence?.scan_state === "clean");
  const blocking = uploads.filter((u) => u.state === "uploading" || (u.state === "done" && u.evidence?.scan_state !== "clean"));
  const slotsLeft = EVIDENCE_MAX_ITEMS - uploads.filter((u) => u.state === "done" || u.state === "uploading").length;
  const stub = uploads.some((u) => u.evidence?.stub_scanner);

  function submit() {
    setFormError(null);
    const r = reason.trim();
    if (r.length < 1) return setReasonError("Explain what's wrong with this line.");
    if (r.length > REASON_MAX_CHARS) return setReasonError(`Keep it to ${REASON_MAX_CHARS.toLocaleString()} characters.`);
    setReasonError(undefined);
    if (blocking.length) return setFormError("Remove the files that are still uploading or that didn't pass the check.");
    if (attachable.length > EVIDENCE_MAX_ITEMS) return setFormError(`Attach at most ${EVIDENCE_MAX_ITEMS} files.`);
    file.mutate({ reason: r, evidence_ids: Array.from(new Set(attachable.map((u) => u.evidence!.evidence_id))) });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!file.isPending}
      title={`Dispute line ${line.line_no}`}
      description={
        <>
          {line.description} · <Money value={line.amount} currency={line.currency} />
        </>
      }
      actions={
        filed ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={file.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} loading={file.isPending}>
              Submit dispute
            </Button>
          </>
        )
      }
    >
      {filed ? (
        <Banner tone="success" title="Dispute filed" live="polite">
          A reviewer will look at it. You'll see the outcome on the dispute and on this statement.{" "}
          <Link to={disputesPath(home, filed.dispute.dispute_id)}>Open the dispute</Link>
        </Banner>
      ) : (
        <div className="stack">
          {line.dispute_eligible_until ? (
            <p className="small">
              You can dispute this line until <strong>{formatLocal(line.dispute_eligible_until)}</strong>. After that it's refused outright;
              there's no late-dispute process yet. Each line can be disputed once.
            </p>
          ) : null}
          {partyType === "advertiser" ? (
            <div className="stack-sm">
              <Button size="sm" variant="ghost" onClick={() => setShowEvents((s) => !s)} aria-expanded={showEvents}>
                {showEvents ? "Hide the billed event" : "Show the billed event behind this line"}
              </Button>
              {showEvents ? (
                events.isPending ? (
                  <p className="small muted">Loading…</p>
                ) : events.isError ? (
                  <CommandError error={events.error} />
                ) : events.data.events.length === 0 ? (
                  <p className="small muted">No billed event is tied to this line.</p>
                ) : (
                  <Table
                    caption="Billed event behind this line"
                    rows={events.data.events}
                    rowKey={(e) => e.reservation_id}
                    columns={[
                      { key: "when", header: "Decided", cell: (e) => formatLocal(e.decided_at) },
                      { key: "model", header: "Pricing", cell: (e) => e.pricing_model.toUpperCase() },
                      { key: "state", header: "State", cell: (e) => e.reservation_state.replace(/_/g, " ") },
                      { key: "amt", header: "Amount", cell: (e) => <Money value={e.amount} currency={e.currency} />, numeric: true },
                    ]}
                  />
                )
              ) : null}
            </div>
          ) : null}
          <Textarea
            label="What's wrong with this line?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            maxLength={REASON_MAX_CHARS}
            error={reasonError}
            hint={`${reason.trim().length.toLocaleString()} of ${REASON_MAX_CHARS.toLocaleString()} characters.`}
          />
          <FieldShell label="Evidence" optional hint={EVIDENCE_LIMITS_TEXT}>
            {({ id, describedBy }) => (
              <input
                id={id}
                ref={fileInput}
                type="file"
                multiple
                accept={EVIDENCE_ACCEPT}
                aria-describedby={describedBy}
                disabled={slotsLeft <= 0}
                onChange={(e) => void onFiles(e)}
              />
            )}
          </FieldShell>
          {uploads.length > 0 ? (
            <ul className="progress-list" aria-label="Evidence files">
              {uploads.map((u) => (
                <li key={u.key} className="progress-item" aria-label={u.name}>
                  <div className="stack-sm" style={{ flex: 1 }}>
                    <div className="row-between">
                      <span className="break-all">
                        {u.name} <span className="xs muted">({u.evidence ? `${MEDIA_LABEL[u.evidence.media_type]}, ` : ""}{formatBytes(u.size)})</span>
                      </span>
                      {u.state === "uploading" ? (
                        <StatusPill tone="info" icon="clock">
                          Uploading
                        </StatusPill>
                      ) : u.state === "done" && u.evidence ? (
                        <StatusPill tone={SCAN_STATE[u.evidence.scan_state].tone}>{SCAN_STATE[u.evidence.scan_state].label}</StatusPill>
                      ) : (
                        <StatusPill tone="error">Not uploaded</StatusPill>
                      )}
                    </div>
                    {u.localError ? <span className="small field-error">{u.localError}</span> : null}
                    {u.state === "failed" ? (
                      <CommandError error={u.error}>
                        <RejectionDetail error={u.error} home={home} />
                      </CommandError>
                    ) : null}
                    {u.evidence?.scan_state === "rejected" ? (
                      <span className="small">This file can't be attached. Remove it, and upload a different copy if you need to.</span>
                    ) : null}
                    {u.evidence?.scan_state === "quarantined" ? (
                      <span className="small">Still being checked. It can be attached once it passes.</span>
                    ) : null}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setUploads((us) => us.filter((x) => x.key !== u.key))} aria-label={`Remove ${u.name}`}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          {stub ? <p className="xs muted">{STUB_SCANNER_TEXT}</p> : null}
          {formError ? (
            <Banner tone="error" live="alert">
              {formError}
            </Banner>
          ) : null}
          {file.isError ? (
            <CommandError error={file.error}>
              <RejectionDetail error={file.error} home={home} />
            </CommandError>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
